import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PWAThunk } from 'lib/core/data'
import { GTM } from 'lib/core/services/gtm'
import { getCart } from 'lib/core/data/quote/cart/selectors'
import { Slice, PlaceOrderSlice, PlaceOrderOutput } from './types'
import { getOrderId } from './selectors'
import { completeCart } from 'lib/core/data/quote/cart'
import { Ga4Events } from '../../../../gtm/index'

export const initialState: Slice = {}

interface ActionData {
  cartId: string
  data?: PlaceOrderOutput
  message?: string
}

const getSlice = (slice: PlaceOrderSlice): PlaceOrderSlice => {
  return slice ?? ({
    isFetching: false,
    isSuccess: false,
    isFailure: false,
    payload: undefined
  })
}

const slice = createSlice({
  name: 'quote/placeOrderNext',
  initialState,
  reducers: {
    request (state, action: PayloadAction<ActionData>) {
      const { cartId } = action.payload
      const slice = getSlice(state[cartId])
      slice.isFetching = true
      state[cartId] = slice
    },

    success (state, action: PayloadAction<ActionData>) {
      const { cartId, data } = action.payload
      const slice = getSlice(state[cartId])
      slice.isFetching = false
      slice.isSuccess = true
      slice.isFailure = false
      slice.payload = data
    },

    failure (state, action: PayloadAction<ActionData>) {
      const { cartId, data, message } = action.payload
      const slice = getSlice(state[cartId])
      slice.isFetching = false
      slice.isSuccess = false
      slice.isFailure = true
      slice.payload = data
      slice.message = message
    }
  }
})

export const reducer = slice.reducer

export const { request, success, failure } = slice.actions

export function placeOrder (cartId: string): PWAThunk {
  return async (dispatch, getState, params) => {
    const { magentoClient } = params
    dispatch(request({
      cartId
    }))

    try {
      const response = await magentoClient.getClient().quote.placeOrderNext({ cartId })

      dispatch(completeCart({ cartId }))

      const { data } = response
      if (data?.placeOrder?.order?.order_id) {
        dispatch(success({
          cartId,
          data
        }))

        const state = getState()
        const orderId = getOrderId(cartId)(state)
        const cart = getCart(cartId)(state)

        if (orderId && cart?.data) {
          GTM.purchase({
            orderId,
            cart: cart.data
          })
          Ga4Events.purchase({
            orderId,
            cart: cart.data
          })
        }
      } else {
        dispatch(failure({
          cartId,
          data
        }))
      }

      return response
    } catch (e) {
      dispatch(failure({
        cartId,
        message: e.message
      }))
    }
  }
}
